import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Crown, Inbox, LoaderCircle, Mail, Reply, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isAdminRole } from "@/lib/adminRoles";
import { SUGGESTION_STATUS_META } from "@/components/dashboard/DashboardLayout";
import { EASE } from "@/components/landing/Reveal";

const STATUSES = ["nouvelle", "en_cours", "traitee", "rejetee"];
const PRIORITIES = [
  { value: "haute", label: "Haute", cls: "text-rouge" },
  { value: "normale", label: "Normale", cls: "text-slate-600" },
  { value: "basse", label: "Basse", cls: "text-slate-400" },
];

function SuggestionCard({ s, onChanged }) {
  const [reply, setReply] = useState("");
  const [showReply, setShowReply] = useState(false);
  const [sending, setSending] = useState(false);
  const st = SUGGESTION_STATUS_META[s.status] || SUGGESTION_STATUS_META.nouvelle;
  const prio = PRIORITIES.find((p) => p.value === s.priority) || PRIORITIES[1];

  const patch = async (fields) => {
    try {
      await api.patch(`/admin/suggestions/${s.id}`, fields);
      onChanged();
    } catch {
      toast.error("Impossible de mettre à jour la suggestion.");
    }
  };

  const sendReply = async () => {
    if (reply.trim().length < 2 || sending) return;
    setSending(true);
    try {
      const { data } = await api.post(`/admin/suggestions/${s.id}/reply`, { message: reply.trim() });
      toast.success(data.emailed
        ? "Réponse enregistrée et envoyée par email à l'utilisateur."
        : "Réponse enregistrée (email non délivré, voir les logs).");
      setReply("");
      setShowReply(false);
      onChanged();
    } catch {
      toast.error("Impossible d'envoyer la réponse.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={`card-integra p-5 ${s.priority === "haute" ? "border-rouge/25" : ""}`} data-testid="admin-suggestion-card">
      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded-full bg-bleu-light px-2.5 py-1 text-[10px] font-bold text-bleu">{s.category}</span>
        <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold ${st.cls}`} data-testid="admin-suggestion-status-chip">{st.label}</span>
        <span className="text-[11px] text-slate-400">{new Date(s.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })}</span>
        <div className="ml-auto flex items-center gap-2">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Priorité</label>
          <select value={s.priority} onChange={(e) => patch({ priority: e.target.value })}
            className={`rounded-lg border border-slate-200 px-2 py-1 text-[11px] font-bold focus:outline-none ${prio.cls}`}
            data-testid="admin-suggestion-priority-select">
            {PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
          <select value={s.status} onChange={(e) => patch({ status: e.target.value })}
            className="rounded-lg border border-slate-200 px-2 py-1 text-[11px] font-semibold text-slate-600 focus:outline-none"
            data-testid="admin-suggestion-status-select">
            {STATUSES.map((v) => <option key={v} value={v}>{SUGGESTION_STATUS_META[v].label}</option>)}
          </select>
        </div>
      </div>

      <p className="mt-3 text-sm leading-relaxed text-slate-800" data-testid="admin-suggestion-message">{s.message}</p>

      <p className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px] text-slate-400" data-testid="admin-suggestion-user">
        <Mail className="h-3 w-3" />
        {s.user ? (
          <>
            <span className="font-semibold text-slate-500">{s.user.name}</span> · {s.user.email}
            {s.user.plan === "premium" && <span className="flex items-center gap-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold text-amber-700"><Crown className="h-2.5 w-2.5" /> Premium</span>}
          </>
        ) : "Compte supprimé"}
      </p>

      {s.admin_response && (
        <div className="mt-3 rounded-xl bg-bleu-light/70 p-3" data-testid="admin-suggestion-response">
          <p className="text-[10px] font-bold uppercase tracking-wider text-bleu">
            Réponse envoyée {s.admin_response_at && `— ${new Date(s.admin_response_at).toLocaleDateString("fr-FR")}`}
          </p>
          <p className="mt-1 text-xs leading-relaxed text-slate-700">{s.admin_response}</p>
        </div>
      )}

      {showReply ? (
        <div className="mt-3 space-y-2">
          <textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={3} maxLength={2000} autoFocus
            placeholder="Votre réponse à l'utilisateur (visible dans son espace + envoyée par email)…"
            className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm placeholder:text-slate-400 focus:border-bleu focus:outline-none focus:ring-2 focus:ring-bleu/15"
            data-testid="admin-reply-input" />
          <div className="flex gap-2">
            <button onClick={sendReply} disabled={sending || reply.trim().length < 2}
              className="btn-primary !px-5 !py-2 !text-xs disabled:opacity-50" data-testid="admin-reply-send">
              {sending ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : <><Send className="h-3.5 w-3.5" /> Envoyer la réponse</>}
            </button>
            <button onClick={() => setShowReply(false)} className="rounded-full border border-slate-200 px-4 text-xs font-semibold text-slate-500" data-testid="admin-reply-cancel">Annuler</button>
          </div>
        </div>
      ) : (
        <button onClick={() => setShowReply(true)}
          className="mt-3 flex items-center gap-1.5 rounded-full border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600 transition-colors hover:border-bleu/40 hover:text-bleu"
          data-testid="admin-reply-open">
          <Reply className="h-3.5 w-3.5" /> {s.admin_response ? "Répondre à nouveau" : "Répondre"}
        </button>
      )}
    </div>
  );
}

export default function AdminSuggestions() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/admin/suggestions", { params: filter ? { status: filter } : {} });
      setData(d);
    } catch {
      toast.error("Impossible de charger les suggestions.");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    if (isAdminRole(user)) load();
  }, [user, load]);

  if (user && !isAdminRole(user)) return <Navigate to="/dashboard" replace />;

  const chips = [
    { value: "", label: `Toutes (${data?.total ?? 0})` },
    ...STATUSES.map((s) => ({ value: s, label: `${SUGGESTION_STATUS_META[s].label} (${data?.counts?.[s] ?? 0})` })),
  ];

  return (
    <div className="mx-auto max-w-3xl space-y-6" data-testid="page-admin-suggestions">
      <div>
        <h1 className="flex items-center gap-2.5 font-display text-2xl font-extrabold text-slate-900 sm:text-3xl">
          <Inbox className="h-7 w-7 text-bleu" /> Boîte à suggestions
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Espace administrateur : lisez, priorisez et répondez aux suggestions des utilisateurs.
          Les réponses sont visibles dans leur espace et envoyées par email.
        </p>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="admin-suggestions-filters">
        {chips.map((c) => (
          <button key={c.value} onClick={() => setFilter(c.value)}
            className={`rounded-full px-4 py-2 text-xs font-bold transition-colors ${
              filter === c.value ? "bg-bleu text-white" : "border border-slate-200 bg-white text-slate-500 hover:border-bleu/40 hover:text-bleu"
            }`}
            data-testid={`admin-filter-${c.value || "all"}`}>
            {c.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><LoaderCircle className="h-6 w-6 animate-spin text-slate-300" /></div>
      ) : !data || data.suggestions.length === 0 ? (
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: EASE }}
          className="card-integra p-10 text-center" data-testid="admin-suggestions-empty">
          <Inbox className="mx-auto h-10 w-10 text-slate-200" />
          <p className="mt-3 text-sm font-bold text-slate-700">Aucune suggestion {filter ? "avec ce statut" : "pour le moment"}</p>
          <p className="mt-1 text-xs text-slate-400">Les suggestions envoyées par les utilisateurs apparaîtront ici.</p>
        </motion.div>
      ) : (
        <div className="space-y-4">
          {data.suggestions.map((s) => <SuggestionCard key={s.id} s={s} onChanged={load} />)}
        </div>
      )}
    </div>
  );
}
